import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface GlossaryTerm {
  id: number;
  slug: string;
  termo: string;
  categoria: string | null;
  tag: string | null;
  o_que_e: string | null;
  analogia: string | null;
  onde_usa: string | null;
  fonte: string | null;
  criado_em: string;
}

export interface GlossaryStats {
  total_termos: number;
  total_categorias: number;
  total_matches: number;
}

export interface GlossaryListResponse {
  terms: GlossaryTerm[];
  stats: GlossaryStats;
}

export function useGlossaryList(params: {
  q?: string;
  categoria?: string;
} = {}): UseQueryResult<GlossaryListResponse> {
  const search = new URLSearchParams();
  if (params.q && params.q.length >= 2) search.set('q', params.q);
  if (params.categoria) search.set('categoria', params.categoria);
  const qs = search.toString();
  return useQuery({
    queryKey: ['glossary', 'list', params],
    queryFn: () => api<GlossaryListResponse>(`/api/glossary${qs ? `?${qs}` : ''}`),
  });
}

export function useGlossaryCategorias(): UseQueryResult<{
  categorias: Array<{ categoria: string; total: number }>;
}> {
  return useQuery({
    queryKey: ['glossary', 'categorias'],
    queryFn: () =>
      api<{ categorias: Array<{ categoria: string; total: number }> }>('/api/glossary/categorias'),
  });
}

export function useGlossaryTerm(slug: string | null | undefined): UseQueryResult<{
  term: GlossaryTerm;
  items: Array<{ item_id: number; nome: string; tipo: string; campo: string }>;
}> {
  return useQuery({
    queryKey: ['glossary', 'term', slug],
    enabled: !!slug,
    queryFn: () =>
      api<{
        term: GlossaryTerm;
        items: Array<{ item_id: number; nome: string; tipo: string; campo: string }>;
      }>(`/api/glossary/${slug}`),
  });
}

export function useSeedGlossary(): ReturnType<
  typeof useMutation<
    { total: number; novos: number; atualizados: number },
    Error,
    { file_path?: string } | undefined
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api<{ total: number; novos: number; atualizados: number }>('/api/glossary/seed', {
        method: 'POST',
        body: body ?? {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['glossary'] }),
  });
}

export function useCrossGlossary(): ReturnType<
  typeof useMutation<
    { termos_processados: number; matches_novos: number; total_matches: number },
    Error
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ termos_processados: number; matches_novos: number; total_matches: number }>(
        '/api/glossary/cross',
        { method: 'POST' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['glossary'] }),
  });
}

export function useUpsertGlossaryTerm(): ReturnType<
  typeof useMutation<
    GlossaryTerm,
    Error,
    {
      termo: string;
      categoria?: string | null;
      tag?: string | null;
      o_que_e?: string | null;
      analogia?: string | null;
      onde_usa?: string | null;
    }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api<GlossaryTerm>('/api/glossary', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['glossary'] }),
  });
}
